import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Field, FlexControlledForm } from '@common-fe/common-fe';

import { useGetPaymentMethods } from '@/modules/transaction/components/ReimburseMe/queries/useGetPaymentMethods.query';
import { PaymentMethodVerificationStatus } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { PlaidDirectDepositBanner } from '@/modules/user/components/ManageProfile/components/ReimbursementOptions/components/PaymentMethodsList/PlaidDirectDepositBanner';
import { PaymentMethodType } from '@/modules/user/user.types';

import { useMakeContributionPaymentMethodField } from '../hooks/useMakeContributionPaymentMethodField';

import { AddContributionBankAccountModal } from './AddContributionBankAccountModal';

interface Props {
  isSubmit: boolean;
  handleChangeValues: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: any,
    fields: Field[],
    handleChangeValues: (val: boolean) => void,
    isPayment?: boolean,) => void;
  setPaymentMethodSetup: (value: boolean) => void;
  currentPaymentMethodId?: number | string;
  setPaymentMethodValid?: (value: boolean) => void;
}

export const MakeContributionPaymentMethodForm: React.FC<Props> = ({
  isSubmit,
  handleChangeValues,
  setPaymentMethodSetup,
  currentPaymentMethodId,
  setPaymentMethodValid,
}) => {
  const [addBankAccountModalVisible, setAddBankAccountModalVisible] = useState(false);
  const [newBankAccountId, setNewBankAccountId] = useState<string | undefined>(undefined);
  const directDepositPaymentMethods = useGetPaymentMethods(
    undefined,
    PaymentMethodType.DIRECT_DEPOSIT,
  );

  const paymentOptions = useMemo(() => directDepositPaymentMethods?.formattedData?.map((method) => ({
    key: `${method.id}`,
    value: method.id,
    title: `${method.accountDescription || 'Without name'} (ending in ${method.accountNumberLast4 || '••••'})`,
  })), [directDepositPaymentMethods]);

  const paymentMethodField = useMakeContributionPaymentMethodField(
    paymentOptions,
    () => setAddBankAccountModalVisible(true),
    newBankAccountId,
    currentPaymentMethodId ? `${currentPaymentMethodId}` : undefined,
  );

  const paymentMethodVerificationStatus = useMemo(() => {
    const currentPaymentMethod = directDepositPaymentMethods?.data?.find(
      (method) => `${method.id}` === `${currentPaymentMethodId}`,
    );
    if (currentPaymentMethod?.verification_status === PaymentMethodVerificationStatus.PENDING
      || currentPaymentMethod?.verification_status === PaymentMethodVerificationStatus.PENDING_MANUAL
      || currentPaymentMethod?.verification_status === PaymentMethodVerificationStatus.VERIFICATION_FAILED
      || currentPaymentMethod?.verification_status === PaymentMethodVerificationStatus.EXPIRED) {
      return currentPaymentMethod?.verification_status;
    }
    return undefined;
  }, [currentPaymentMethodId, directDepositPaymentMethods]);

  const currentMethodAccountId = useMemo(() => {
    const currentPaymentMethod = directDepositPaymentMethods?.data?.find(
      (method) => `${method.id}` === `${currentPaymentMethodId}`,
    );
    return currentPaymentMethod?.account_id ? `${currentPaymentMethod?.account_id}` : undefined;
  }, [currentPaymentMethodId, directDepositPaymentMethods]);

  const isPendingAccountExists = useMemo(() => {
    return directDepositPaymentMethods?.data?.some(
      (method) => method.verification_status === PaymentMethodVerificationStatus.PENDING
        || method.verification_status === PaymentMethodVerificationStatus.PENDING_MANUAL,
    );
  }, [directDepositPaymentMethods?.data]);

  const handleChangePaymentMethodValues = useCallback((values: object) => {
    handleChangeValues(values, paymentMethodField, setPaymentMethodSetup, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (setPaymentMethodValid && paymentMethodVerificationStatus) {
      setPaymentMethodValid(false);
      return;
    }

    if (setPaymentMethodValid && !paymentMethodVerificationStatus) {
      setPaymentMethodValid(true);
    }
  }, [currentPaymentMethodId, paymentMethodVerificationStatus, setPaymentMethodValid]);
  return (
    <>
      <Box data-testid="contribution-payment-method-form" round="container1Round" background="canvas">
        <FlexControlledForm
          fields={paymentMethodField}
          editMode
          showError={isSubmit}
          onChangeValues={handleChangePaymentMethodValues}
          isModalType
          footer={paymentMethodVerificationStatus && (
            <Box margin={{ top: 'spacing16' }} pad={{ horizontal: 'spacing24', bottom: 'spacing24' }}>
              <PlaidDirectDepositBanner
                status={paymentMethodVerificationStatus}
                highlightedTop
                withoutButton
                accountId={currentMethodAccountId}
                refetch={directDepositPaymentMethods?.refetch}
              />
            </Box>
          )}
        />
      </Box>
      <AddContributionBankAccountModal
        visible={addBankAccountModalVisible}
        onSetVisible={setAddBankAccountModalVisible}
        setCurrentBankAccount={setNewBankAccountId}
        isPendingAccountExists={isPendingAccountExists}
        refetch={directDepositPaymentMethods?.refetch}
      />
    </>
  );
};
